'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface StatItem {
  id: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

interface CountryItem {
  id: string;
  name: string;
  icon: string;
}

interface GlobalReachSectionProps {
  title: string;
  subtitle: string;
  stats: StatItem[];
}

const countries: CountryItem[] = [
  { id: '1', name: 'UK', icon: 'uk' },
  { id: '2', name: 'USA', icon: 'usa' },
  { id: '3', name: 'Europe', icon: 'europe' },
  { id: '4', name: 'Africa', icon: 'africa' },
  { id: '5', name: 'Singapore', icon: 'singapore' },
  { id: '6', name: 'Australia', icon: 'australia' },
  { id: '7', name: 'India', icon: 'india' },
  { id: '8', name: 'Canada', icon: 'canada' },
];

// Country landmark SVG icons (from /public/assets/images)
const CountryIcon = ({ type, className = '' }: { type: string; className?: string }) => {
  const assetByType: Record<
    string,
    { src: string; raster?: boolean; alt: string; tintMode?: 'filter' | 'blend' }
  > = {
    uk: { src: 'London-white.svg', alt: 'UK landmark' },
    usa: { src: 'New-York-white.svg', alt: 'USA landmark' },
    europe: { src: 'Athens-White.svg', alt: 'Europe landmark' },
    africa: { src: 'South-Africa-white.svg', alt: 'Africa landmark' },
    singapore: { src: 'Singapore-white.svg', alt: 'Singapore landmark' },
    australia: { src: 'sydney-white2.svg', alt: 'Australia landmark' },
    india: { src: 'lotus-temple.png', raster: true, alt: 'India landmark', tintMode: 'filter' },
    canada: { src: 'cn-tower.png', raster: true, alt: 'Canada landmark', tintMode: 'filter' },
  };

  const asset = assetByType[type];
  if (!asset) return null;

  if (asset.raster) {
     

    // lotus-temple.png has a transparent background — alpha mask renders it as pink-500.
    return (
      <div
        aria-hidden="true"
        className={`${className} bg-pink-500`}
        style={{
          WebkitMaskImage: `url(/assets/images/${asset.src})`,
          maskImage: `url(/assets/images/${asset.src})`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }}
      />
    );
  }

  // Use mask so we can tint the SVG to pink reliably.
  return (
    <div
      aria-hidden="true"
      className={`${className} bg-pink-500`}
      style={{
        WebkitMaskImage: `url(/assets/images/${asset.src})`,
        maskImage: `url(/assets/images/${asset.src})`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }}
    />
  );
};

export default function GlobalReachSection({ title, subtitle, stats }: GlobalReachSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedValues, setAnimatedValues] = useState<Record<string, number>>({});
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const observedElement = sectionRef.current;
    if (observedElement) {
      observer.observe(observedElement);
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement);
      }
    };
  }, [isHydrated]);

  // Stats animation
  useEffect(() => {
    if (!isVisible || !isHydrated) return;

    const initialValues: Record<string, number> = {};
    stats.forEach((stat) => {
      initialValues[stat.id] = 0;
    });
    setAnimatedValues(initialValues);

    stats.forEach((stat) => {
      const duration = 2000;
      const steps = 60;
      const increment = stat.value / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setAnimatedValues((prev) => ({
            ...prev,
            [stat.id]: Math.min(Math.round(increment * currentStep), stat.value),
          }));
        } else {
          clearInterval(timer);
        }
      }, duration / steps);
    });
  }, [isVisible, stats, isHydrated]);

  if (!isHydrated) {
    return (
      <section className="py-12 md:py-16 lg:py-24 bg-background relative overflow-hidden">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {title}
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">{subtitle}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="global-reach"
      className="py-12 md:py-16 lg:py-24 bg-gradient-to-br from-pink-50 via-slate-50 to-blue-50 relative overflow-hidden isolate"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,rgba(250,0,172,0.16),transparent_42%),radial-gradient(circle_at_84%_18%,rgba(39,78,255,0.15),transparent_40%),radial-gradient(circle_at_50%_90%,rgba(16,185,129,0.14),transparent_43%)]" />
        <div className="absolute -top-20 -left-16 w-72 h-72 bg-pink-500/18 rounded-full blur-3xl animate-float-soft" />
        <div
          className="absolute top-16 -right-16 w-80 h-80 bg-blue-500/16 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '700ms' }}
        />
        <div className="absolute -bottom-16 left-1/3 w-80 h-80 bg-emerald-500/16 rounded-full blur-3xl animate-float-soft" />
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 relative z-10">
        <div className="text-center mb-8 md:mb-10 lg:mb-14 animate-slide-down">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full border border-pink-200/70 bg-white/80 shadow-sm backdrop-blur-md">
            <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] text-pink-600">
              Global Presence
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-[0.95rem] sm:text-base lg:text-[1.05rem] text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="rounded-[2rem] border border-white/70 bg-white/70 backdrop-blur-xl shadow-[0_24px_70px_-30px_rgba(39,78,255,0.35)] p-4 sm:p-5 md:p-7 mb-8 md:mb-12">
          <div className="overflow-hidden relative group">
            <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white/95 to-transparent z-10 hidden md:block" />
            <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white/95 to-transparent z-10 hidden md:block" />
            <div className="flex space-x-8 md:space-x-12 animate-scroll-countries whitespace-nowrap py-3 md:py-4 px-2 sm:px-4 md:px-5">
              {[...countries, ...countries, ...countries, ...countries].map((country, idx) => (
                <div
                  key={`${country.id}-${idx}`}
                  className="flex-shrink-0 flex flex-col items-center gap-3 md:gap-5 group/item transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="relative">
                    <CountryIcon
                      type={country.icon}
                      className="w-20 h-28 md:w-28 md:h-36 object-contain opacity-40 group-hover/item:opacity-100 transition-all duration-500 group-hover/item:scale-110 drop-shadow-xl"
                    />
                    <div className="absolute inset-0 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover/item:opacity-100 transition-opacity duration-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-muted-foreground group-hover/item:text-foreground transition-colors duration-300">
                    {country.name}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-white/80 shadow-[0_14px_40px_-20px_rgba(0,0,0,0.25)] transition-all duration-500 hover:-translate-y-2 hover:border-pink-200 hover:shadow-[0_24px_60px_-24px_rgba(250,0,172,0.45)] cursor-default animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                <div className="absolute -inset-16 bg-pink-500/14 blur-3xl" />
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/15 via-transparent to-blue-500/12" />
              </div>

              <div className="relative z-10">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-pink-500/12 rounded-xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                  <Icon
                    name={stat.icon}
                    size={24}
                    className="text-primary transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2 transition-all duration-500 group-hover:scale-105">
                  {isVisible ? animatedValues[stat.id] || 0 : 0}
                  {stat.suffix}
                </div>
                <div className="text-sm font-semibold text-muted-foreground">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx global>{`
        @keyframes scroll-countries {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-25%);
          }
        }
        .animate-scroll-countries {
          animation: scroll-countries 40s linear infinite;
          display: flex;
          width: max-content;
        }
        .animate-scroll-countries:hover {
          animation-play-state: paused;
        }
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-12px);
          }
        }
        @keyframes float-soft {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-10px) translateX(6px);
          }
        }
        @keyframes slide-down {
          0% {
            transform: translateY(-20px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slide-up {
          0% {
            transform: translateY(20px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </section>
  );
}
